import React, { useState, useEffect } from 'react';
import { Form, Image } from 'react-bootstrap';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { createLogger } from '../../utils/logger';

const logger = createLogger('SupermarketDropdown');

export default function SupermarketDropdown({
  supermarketOptions,
  supermarketFilter,
  onSupermarketFilterChange,
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [supermarketImages, setSupermarketImages] = useState({});

  const responsiveImageStyle = {
    width: '32px',
    height: '32px',
    objectFit: 'cover',
  };

  const responsiveIconStyle = {
    width: '32px',
    height: '32px',
    backgroundColor: '#e9ecef',
    color: '#6c757d',
  };

  useEffect(() => {
    const fetchImages = async () => {
      const imagePromises = supermarketOptions.map(async (option) => {
        if (option.media && option.media.length > 0 && option.media[0].url) {
          try {
            const storage = getStorage();
            const imageRef = ref(storage, option.media[0].url);
            const imageUrl = await getDownloadURL(imageRef);
            setSupermarketImages((prev) => ({ ...prev, [option.id]: imageUrl }));
          } catch (imageError) {
            logger.warn(`Failed to load image for supermarket ${option.id}:`, imageError);
          }
        }
      });
      await Promise.all(imagePromises);
    };

    if (supermarketOptions.length > 0) {
      fetchImages();
    }
  }, [supermarketOptions]);

  const selectedOption = supermarketOptions.find((opt) => opt.id === supermarketFilter) || {
    id: '',
    display_name: 'All Supermarkets',
    address: '',
  };

  return (
    <>
      <style>
        {`
            .supermarket-dropdown-input {
              min-height: 60px;
              overflow: hidden;
              position: relative;
              width: 100%;
            }
            .supermarket-dropdown-input > div {
              width: 100%;
              overflow: hidden;
            }
            .supermarket-dropdown-input .flex-grow-1 {
              background-color: transparent;
              position: relative;
              z-index: 1;
            }
            @media (max-width: 767.98px) {
              .supermarket-dropdown-input {
                min-height: 50px !important;
                font-size: 0.9rem !important;
                padding: 0.5rem 0.75rem !important;
                width: 100% !important;
              }
              .supermarket-dropdown-input .fw-semibold {
                font-size: 0.9rem !important;
              }
              .supermarket-dropdown-input .small {
                font-size: 0.75rem !important;
              }
            }
            @media (max-width: 575.98px) {
              .supermarket-dropdown-input {
                min-height: 45px !important;
                font-size: 0.85rem !important;
                padding: 0.4rem 0.6rem !important;
                width: 100% !important;
              }
              .supermarket-dropdown-input .fw-semibold {
                font-size: 0.85rem !important;
              }
              .supermarket-dropdown-input .small {
                font-size: 0.7rem !important;
              }
            }
            @media (max-width: 767.98px) {
              .supermarket-dropdown-option {
                padding: 0.5rem 0.75rem !important;
              }
              .supermarket-dropdown-option .fw-semibold {
                font-size: 0.9rem !important;
              }
              .supermarket-dropdown-option .small {
                font-size: 0.75rem !important;
              }
            }
            @media (max-width: 575.98px) {
              .supermarket-dropdown-option {
                padding: 0.4rem 0.6rem !important;
              }
              .supermarket-dropdown-option .fw-semibold {
                font-size: 0.85rem !important;
              }
              .supermarket-dropdown-option .small {
                font-size: 0.7rem !important;
              }
            }
          `}
      </style>
      <div className="position-relative w-100">
        <Form.Label className="fw-semibold mb-2 mb-md-3">Filter by Supermarket</Form.Label>
        <div
          className="form-control d-flex align-items-center justify-content-between cursor-pointer supermarket-dropdown-input"
          onClick={() => setShowDropdown(!showDropdown)}
          style={{
            minHeight: '60px',
            cursor: 'pointer',
            backgroundColor: '#fff',
            border: '1px solid #ced4da',
            borderRadius: '0.375rem',
            transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
            padding: '0.75rem 1rem',
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = '#86b7fe';
            e.target.style.boxShadow = '0 0 0 0.25rem rgba(13, 110, 253, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = '#ced4da';
            e.target.style.boxShadow = 'none';
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowDropdown((open) => !open);
            }
          }}
        >
          <div className="d-flex align-items-center flex-grow-1 min-width-0">
            {selectedOption.id && supermarketImages[selectedOption.id] && (
              <Image
                src={supermarketImages[selectedOption.id]}
                alt={selectedOption.display_name}
                className="me-2 me-md-3 rounded flex-shrink-0"
                style={responsiveImageStyle}
                onError={(e) => {
                  logger.warn(`Image failed to load for supermarket ${selectedOption.id}`);
                  e.target.style.display = 'none';
                }}
              />
            )}
            {!selectedOption.id && (
              <div
                className="me-2 me-md-3 rounded d-flex align-items-center justify-content-center flex-shrink-0"
                style={responsiveIconStyle}
              >
                <i className="fas fa-store" style={{ fontSize: '14px' }} />
              </div>
            )}
            <div className="flex-grow-1 min-width-0 overflow-hidden">
              <div className="fw-semibold text-truncate" title={selectedOption.display_name}>
                {selectedOption.display_name}
              </div>
              {selectedOption.address && (
                <div className="small text-muted text-truncate" title={selectedOption.address}>
                  {selectedOption.address}
                </div>
              )}
              {!selectedOption.address && selectedOption.id && (
                <div className="small text-muted text-truncate">
                  {selectedOption.id === ''
                    ? 'View carts from all supermarkets'
                    : 'Supermarket details not available'}
                </div>
              )}
            </div>
          </div>
          <i className={`fas fa-chevron-${showDropdown ? 'up' : 'down'} text-muted flex-shrink-0 ms-2`} />
        </div>

        {showDropdown && (
          <div
            className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-lg"
            style={{
              zIndex: 1000,
              maxHeight: '300px',
              overflowY: 'auto',
              borderTop: 'none',
              borderTopLeftRadius: '0',
              borderTopRightRadius: '0',
              width: '100%',
              left: '0',
              right: '0',
            }}
          >
            <div
              className="p-2 p-md-3 border-bottom cursor-pointer hover-bg-light supermarket-dropdown-option"
              onClick={() => {
                onSupermarketFilterChange('');
                setShowDropdown(false);
              }}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#fff';
              }}
              role="option"
              aria-selected={!supermarketFilter}
            >
              <div className="d-flex align-items-center">
                <div
                  className="me-2 me-md-3 rounded d-flex align-items-center justify-content-center flex-shrink-0"
                  style={responsiveIconStyle}
                >
                  <i className="fas fa-store" style={{ fontSize: '14px' }} />
                </div>
                <div className="flex-grow-1 min-width-0">
                  <div className="fw-semibold text-truncate">All Supermarkets</div>
                  <div className="small text-muted text-truncate">View carts from all supermarkets</div>
                </div>
              </div>
            </div>

            {supermarketOptions.map((option) => (
              <div
                key={option.id}
                className="p-2 p-md-3 border-bottom cursor-pointer hover-bg-light supermarket-dropdown-option"
                onClick={() => {
                  onSupermarketFilterChange(option.id);
                  setShowDropdown(false);
                }}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                }}
                role="option"
                aria-selected={supermarketFilter === option.id}
              >
                <div className="d-flex align-items-center">
                  {supermarketImages[option.id] ? (
                    <Image
                      src={supermarketImages[option.id]}
                      alt={option.display_name}
                      className="me-2 me-md-3 rounded flex-shrink-0"
                      style={responsiveImageStyle}
                      onError={(e) => {
                        logger.warn(`Image failed to load for supermarket ${option.id}`);
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div
                      className="me-2 me-md-3 rounded d-flex align-items-center justify-content-center flex-shrink-0"
                      style={responsiveIconStyle}
                    >
                      <i className="fas fa-store" style={{ fontSize: '14px' }} />
                    </div>
                  )}
                  <div className="flex-grow-1 min-width-0">
                    <div className="fw-semibold text-truncate" title={option.display_name}>
                      {option.display_name}
                    </div>
                    {option.address ? (
                      <div className="small text-muted text-truncate" title={option.address}>
                        {option.address}
                      </div>
                    ) : (
                      <div className="small text-muted">Supermarket details not available</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showDropdown && (
          <div
            className="position-fixed w-100 h-100"
            style={{
              top: 0,
              left: 0,
              zIndex: 999,
              backgroundColor: 'transparent',
            }}
            onClick={() => setShowDropdown(false)}
            aria-hidden="true"
          />
        )}
      </div>
    </>
  );
}
